import { useState, useMemo } from "react";

import PropTypes from "prop-types";

import { strCode } from "../../../../consts";
import { t } from "../../../../services";
import { csvUtil } from "../../../../utils";

import Modal from "../../../Modal/Modal";
import WrapperContainer from "../../../Containers/WrapperContainer";
import FlexContainers from "../../../Containers/FlexContainers";
import SmallButton from "../../../Buttons/SmallButton";

import IncorrectRecordsSummary from "./components/IncorrectRecordsSummary";
import CorrectRecordsSummary from "./components/CorrectRecordsSummary";
import { ImportingModalContainer } from "./styled";
import SummaryTabs from "./components/SummaryTabs";

export const activeIds = {
  INCORRECT: "incorrect",
  CORRECT: "correct"
};

const ImportingSummary = ({
  active,
  onClose,
  data = [],
  successInfo,
  failedInfo,
  onSubmitFile,
  formatRecords,
  ImportedRecordsTableColumns,
  SuccessImportActions
}) => {
  const [activeId, setActiveId] = useState(activeIds.INCORRECT);

  const incorrectRecords = useMemo(() => {
    return data.filter((record) => !record?.correct);
  }, [data]);

  const correctRecords = useMemo(() => {
    return data.filter((record) => record?.correct);
  }, [data]);

  const onDownloadCSV = () => {
    if (!incorrectRecords?.length) {
      return;
    }

    const incorrectRecordsCsv = csvUtil.createCSVFile(formatRecords(incorrectRecords));
    csvUtil.downloadCSVFile(incorrectRecordsCsv, "incorrectRecords");
  };

  const onImport = () => {
    const correctRecordsCsv = csvUtil.createCSVFile(formatRecords(correctRecords));
    onSubmitFile(correctRecordsCsv, formatRecords(correctRecords));
  };

  return (
    <Modal
      id="importing-summary-modal"
      active={active}
      title={t(strCode.IMPORTING_SUMMARY_TITLE)}
      closable={false}
      fillAvailable
      Container={ImportingModalContainer}>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} width={"100%"} gap={24}>
        {incorrectRecords?.length > 0 && correctRecords?.length > 0 && (
          <SummaryTabs
            activeId={activeId}
            setActiveId={setActiveId}
            incorrectLength={incorrectRecords.length}
            correctLength={correctRecords.length}
          />
        )}

        {incorrectRecords?.length > 0 &&
          (!correctRecords.length ||
            (correctRecords?.length > 0 && activeId === activeIds.INCORRECT)) && (
            <IncorrectRecordsSummary
              incorrectRecords={incorrectRecords}
              totalLength={data.length}
              failedInfo={failedInfo}
              onDownloadCSV={onDownloadCSV}
              ImportedRecordsTableColumns={ImportedRecordsTableColumns}
            />
          )}

        {correctRecords?.length > 0 &&
          (!incorrectRecords.length ||
            (incorrectRecords?.length > 0 && activeId === activeIds.CORRECT)) && (
            <CorrectRecordsSummary
              correctRecords={correctRecords}
              totalLength={data.length}
              successInfo={successInfo}
              ImportedRecordsTableColumns={ImportedRecordsTableColumns}
              SuccessImportActions={SuccessImportActions}
            />
          )}

        <WrapperContainer Container={FlexContainers.FlexRowEnd} width="100%" gap={16}>
          <SmallButton secondary onClick={onClose}>
            {t(strCode.CANCEL_IMPORT_BTN)}
          </SmallButton>

          {correctRecords?.length > 0 && (
            <SmallButton onClick={onImport}>
              {incorrectRecords?.length > 0 ? t(strCode.IMPORT_GOOD_BTN) : t(strCode.IMPORT_BTN)}
            </SmallButton>
          )}
        </WrapperContainer>
      </WrapperContainer>
    </Modal>
  );
};

ImportingSummary.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      correct: PropTypes.bool.isRequired
    }).isRequired
  ),
  successInfo: PropTypes.string.isRequired,
  failedInfo: PropTypes.string.isRequired,
  formatRecords: PropTypes.func.isRequired,
  onSubmitFile: PropTypes.func.isRequired,
  ImportedRecordsTableColumns: PropTypes.oneOfType([PropTypes.node, PropTypes.object]).isRequired,
  SuccessImportActions: PropTypes.oneOfType([PropTypes.node, PropTypes.object])
};

export default ImportingSummary;
